import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface CalendarPickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  minDate?: string; // YYYY-MM-DD format
  label?: string;
  required?: boolean;
}

export function CalendarPicker({ value, onChange, minDate, label, required }: CalendarPickerProps) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);

  // Initialize current month based on selected value or today
  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = selectedDate.toISOString().split('T')[0];
    
    // Check if date is before minDate
    if (minDate && dateString < minDate) {
      return;
    }

    onChange(dateString);
    setShowCalendar(false);
  };

  const isDateDisabled = (day: number) => {
    if (!minDate) return false;
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateString = checkDate.toISOString().split('T')[0];
    return dateString < minDate;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= totalDays; day++) {
      const disabled = isDateDisabled(day);
      const today = isToday(day);
      const selected = isSelected(day);

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !disabled && handleDateSelect(day)}
          disabled={disabled}
          className={`
            p-2 text-sm rounded-lg transition-colors relative
            ${disabled 
              ? 'text-gray-300 dark:text-white/20 cursor-not-allowed' 
              : 'text-gray-900 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'
            }
            ${selected 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90' 
              : ''
            }
            ${today && !selected 
              ? 'border border-blue-500 text-blue-600 dark:text-blue-400' 
              : ''
            }
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="relative" ref={calendarRef}>
      {label && (
        <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
          <Calendar className="w-4 h-4 inline mr-1" />
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      {/* Date Input Field */}
      <div
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent cursor-pointer bg-white dark:bg-[#0F0E16] flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-white/50'}>
          {value ? formatDisplayDate(value) : 'Select a date...'}
        </span>
        <Calendar className="w-5 h-5 text-gray-400 dark:text-white/50" />
      </div>

      {/* Calendar Dropdown */}
      {showCalendar && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-[#16151D] border border-gray-300 dark:border-white/8 rounded-lg shadow-2xl p-4 w-80">
          {/* Month/Year Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-white/70 rounded transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-gray-900 dark:text-white">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-white/70 rounded transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Day Labels */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-xs text-gray-500 dark:text-white/50 text-center p-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendarDays()}
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/8 flex justify-between items-center">
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                if (!minDate || today >= minDate) {
                  onChange(today);
                  setShowCalendar(false);
                }
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setShowCalendar(false)}
              className="text-sm text-gray-600 dark:text-white/70 hover:text-gray-700 dark:hover:text-white"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}